// src/services/notificationService.ts
import { 
  Notification, 
  NotificationType, 
  NotificationPriority, 
  NotificationFilter,
  NotificationStats 
} from '../types/notifications';

class NotificationService {
  private static instance: NotificationService;
  private notifications: Notification[] = [];
  private listeners: Array<(notifications: Notification[]) => void> = [];
  private storageKey = 'ufficio-stile-notifications';
  
  private constructor() {
    this.loadFromStorage();
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // Subscription management
  public subscribe(listener: (notifications: Notification[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener([...this.notifications]));
    this.saveToStorage();
  }

  // Notification CRUD operations
  public addNotification(notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>): string {
    const newNotification: Notification = {
      ...notification,
      id: this.generateId(),
      createdAt: new Date(),
      isRead: false,
    };

    this.notifications.unshift(newNotification);
    this.notifyListeners();
    return newNotification.id;
  }

  public markAsRead(notificationId: string): void {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification && !notification.isRead) {
      notification.isRead = true;
      notification.readAt = new Date();
      this.notifyListeners();
    }
  }

  public markAsUnread(notificationId: string): void {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification && notification.isRead) {
      notification.isRead = false;
      notification.readAt = undefined;
      this.notifyListeners();
    }
  }

  public markAllAsRead(): void {
    const now = new Date();
    this.notifications.forEach(n => {
      if (!n.isRead) {
        n.isRead = true;
        n.readAt = now;
      }
    });
    this.notifyListeners();
  }

  public deleteNotification(notificationId: string): void {
    this.notifications = this.notifications.filter(n => n.id !== notificationId);
    this.notifyListeners();
  }

  public clearAllNotifications(): void {
    this.notifications = [];
    this.notifyListeners();
  }

  // Query methods
  public getNotifications(filter?: NotificationFilter): Notification[] {
    let filtered = [...this.notifications];

    if (filter) {
      if (filter.type) {
        filtered = filtered.filter(n => n.type === filter.type);
      }
      if (filter.priority) {
        filtered = filtered.filter(n => n.priority === filter.priority);
      }
      if (filter.isRead !== undefined) {
        filtered = filtered.filter(n => n.isRead === filter.isRead);
      }
      if (filter.dateFrom) {
        filtered = filtered.filter(n => n.createdAt >= filter.dateFrom!);
      }
      if (filter.dateTo) {
        filtered = filtered.filter(n => n.createdAt <= filter.dateTo!);
      }
      if (filter.searchText) {
        const searchLower = filter.searchText.toLowerCase();
        filtered = filtered.filter(n => 
          n.title.toLowerCase().includes(searchLower) ||
          n.message.toLowerCase().includes(searchLower)
        );
      }
    }

    return filtered.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  public getUnreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }

  public getStats(): NotificationStats {
    const total = this.notifications.length;
    const unread = this.getUnreadCount();
    
    const byType = {} as Record<NotificationType, number>;
    const byPriority = {} as Record<NotificationPriority, number>;

    // Initialize counters
    Object.values(NotificationType).forEach(type => {
      byType[type] = 0;
    });
    Object.values(NotificationPriority).forEach(priority => {
      byPriority[priority] = 0;
    });

    // Count notifications
    this.notifications.forEach(n => {
      byType[n.type]++;
      byPriority[n.priority]++;
    });

    return { total, unread, byType, byPriority };
  }

  // Utility methods
  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private saveToStorage(): void {
    try {
      const data = {
        notifications: this.notifications,
        timestamp: Date.now()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save notifications to localStorage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.notifications && Array.isArray(data.notifications)) {
          // Convert date strings back to Date objects
          this.notifications = data.notifications.map((n: any) => ({
            ...n,
            createdAt: new Date(n.createdAt),
            readAt: n.readAt ? new Date(n.readAt) : undefined
          }));
        }
      }
    } catch (error) {
      console.warn('Failed to load notifications from localStorage:', error);
      this.notifications = [];
    }
  }

  // Helper methods for creating common notification types
  public notifyAssignment(requestId: string, projectName: string, assignedTo: string): string {
    return this.addNotification({
      type: NotificationType.Assignment,
      priority: NotificationPriority.High,
      title: 'Nuova Assegnazione',
      message: `Ti è stata assegnata la richiesta "${projectName}"`,
      actionUrl: `/richieste/${requestId}`,
      actionText: 'Visualizza Richiesta',
      metadata: {
        requestId,
        projectName,
        assignedTo
      }
    });
  }

  public notifyTaskCompleted(taskId: string, taskTitle: string, completedBy: string): string {
    return this.addNotification({
      type: NotificationType.TaskCompleted,
      priority: NotificationPriority.Medium,
      title: 'Task Completato',
      message: `Il task "${taskTitle}" è stato completato da ${completedBy}`,
      actionUrl: `/board`,
      actionText: 'Vedi Kanban',
      metadata: {
        taskId,
        taskTitle,
        completedBy
      }
    });
  }

  public notifyDeadlineAlert(requestId: string, projectName: string, daysLeft: number): string {
    const priority = daysLeft <= 1 ? NotificationPriority.Critical : NotificationPriority.High;
    const message = daysLeft === 0 
      ? `La richiesta "${projectName}" scade oggi!`
      : `La richiesta "${projectName}" scade tra ${daysLeft} giorni`;

    return this.addNotification({
      type: NotificationType.DeadlineAlert,
      priority,
      title: 'Scadenza Imminente',
      message,
      actionUrl: `/richieste/${requestId}`,
      actionText: 'Visualizza',
      metadata: {
        requestId,
        projectName,
        daysLeft
      }
    });
  }

  public notifyStatusUpdate(requestId: string, projectName: string, newStatus: string): string {
    return this.addNotification({
      type: NotificationType.StatusUpdate,
      priority: NotificationPriority.Medium,
      title: 'Stato Aggiornato',
      message: `La richiesta "${projectName}" è passata a: ${newStatus}`,
      actionUrl: `/richieste/${requestId}`,
      actionText: 'Visualizza',
      metadata: {
        requestId,
        projectName,
        newStatus
      }
    });
  }

  public notifySystem(message: string, priority: NotificationPriority = NotificationPriority.Low): string {
    return this.addNotification({
      type: NotificationType.System,
      priority,
      title: 'Notifica Sistema',
      message
    });
  }
}

export const notificationService = NotificationService.getInstance();